// --- external
import { computed, provide, ref } from "vue";

// --- internal
import { useBrand } from "../brand";

// --- utils
import {
  isEmpty,
  startCase,
  keyBy,
  merge,
  values,
  isString,
  isArray,
  trim,
  forEach
} from "lodash-es";

// --- types
import type { Theme } from "./types";

// -----------------------------------------------------------------------------
// --- global context

const themes = ref<Theme[] | undefined>(undefined);
// -----------------------------------------------------------------------------
/**
 * Composable for consolidating brand theme information with any provided themes through the upmind initialisation
 *
 * @returns {Object} Theme composable API
 */
export const useTheming = (provided?: Theme | Theme[]) => {
  // First add any themes provided during initialisation

  if (!isEmpty(provided)) addTheme(provided);

  const { meta: brandMeta, uiTheme, isReady } = useBrand();

  // --- context

  // the theme definition

  const meta = computed(() => ({
    isAvailable: brandMeta.value.isAvailable,
    hasThemes: brandMeta.value.isAvailable && !isEmpty(themes.value)
  }));

  // --- methods

  function addTheme(provided: Theme | Theme[]) {
    // ensure we always work with an array
    provided = isArray(provided) ? provided : [provided];

    // then merge to ensure we override any provided themes with brand variant values
    // brand themes are dynamic/from the api so can be updated without requiring a deployment
    const merged = merge({}, keyBy(themes.value, "id"), keyBy(provided, "id"));

    // persist the merged themes
    themes.value = values(merged);
  }

  function extractId(cssString: string): string {
    const lines = cssString.split("\n");
    const selectorLine = lines.find(line => line.includes("[data-theme="));

    if (selectorLine) {
      const quotedValue =
        selectorLine.split("'")[1] || selectorLine.split('"')[1];
      if (quotedValue) return quotedValue;
    }

    return "brand";
  }

  function splitThemes(token: string): Theme[] {
    const themes: Theme[] = [];
    const parts = token.split("[data-theme=").filter(part => part.trim());

    forEach(parts, part => {
      const themeBlock = `[data-theme=${part}`;
      const themeId = extractId(themeBlock);

      themes.push({
        id: themeId,
        name: startCase(themeId),
        uiConfig: {},
        tokens: themeBlock
      });
    });

    return themes;
  }

  // --- side Effects

  isReady().then(() => {
    // variants already contains complete CSS with data-theme selectors
    const variantsString = uiTheme.value?.tokens;
    if (!variantsString || !isString(variantsString)) return;

    addTheme(splitThemes(variantsString));
  });

  // ---------------------------------------------------------------------------

  return {
    // --- state
    isReady,

    /** Meta information about the brand theme state. */
    meta,

    // --- context

    /** The available themes, this will be an array of theme objects.
     * brand config theme variants will be added/merged to the list of provided themes
     */
    themes
  };
};
